/**
 * Client-side API for generating and fetching Neural TTS audio.
 * Calls the /api/generate-tts endpoint which uses Edge TTS + Supabase Storage.
 */
import { sanitizeTextForSpeech, toSpeechLocale } from "./speechText";

const getApiBase = (): string => {
  if (typeof window === "undefined") return "";
  return window.location.origin;
};

export interface GenerateTtsResult {
  audioUrl: string;
  contentType?: string;
}

export interface GenerateTtsOptions {
  text: string;
  locale: string;
  cardId?: string;
  side?: "front" | "back";
  /** API path. Default: /api/generate-audio (fallback: /api/generate-tts) */
  apiPath?: string;
}

/**
 * Request Neural TTS generation. Returns the public Supabase Storage URL.
 * The API generates audio via Edge TTS and uploads to the card_audio bucket.
 */
export async function generateTtsAudio(options: GenerateTtsOptions): Promise<GenerateTtsResult> {
  const { text, locale, cardId, side, apiPath = "/api/generate-audio" } = options;
  const trimmed = sanitizeTextForSpeech(text ?? "");
  if (!trimmed) {
    throw new Error("Text is required");
  }

  const base = getApiBase();
  if (!base) {
    throw new Error("Cannot determine API base URL");
  }

  const payload = {
    text: trimmed,
    locale: toSpeechLocale(locale, "en-US"),
    cardId: cardId || undefined,
    side: side || undefined,
  };
  const body = JSON.stringify(payload);

  let res = await fetch(`${base}${apiPath}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body,
  });

  if (res.status === 404 && apiPath === "/api/generate-audio") {
    res = await fetch(`${base}/api/generate-tts`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
    });
  }

  let data: { audioUrl?: string; contentType?: string; error?: string; errorKey?: string; reason?: string };
  try {
    data = (await res.json()) as typeof data;
  } catch {
    throw new Error("Invalid response from TTS API");
  }
  if (!res.ok) {
    const reason = typeof data.reason === "string" ? data.reason : "";
    const msg = data.errorKey || data.error || `TTS generation failed (${res.status})`;
    const fullMsg = reason ? `${msg}: ${reason}` : msg;
    throw new Error(fullMsg);
  }
  if (!data.audioUrl) {
    throw new Error("No audio URL returned");
  }
  return {
    audioUrl: data.audioUrl,
    contentType: data.contentType,
  };
}
